import orderBy from 'lodash/orderBy';
import { CATEGORIES } from 'modules/app/services/ga/constants';
import GtagService from 'modules/app/services/ga/GtagService';
import { FINISHED_PLAYER_ATTACHMENT } from 'modules/player/constants';
import { UiService } from 'modules/ui';

import { ENTITY_TYPE, ENTITY_DATA_TYPE, UPLOAD_STATUS } from './constants';
import './FileApi';
import { sendDatahubEvent } from './helpers';
import { UploadOrigin } from './types';

import { updateTokenFromAllEntities } from '@hotmart/club-files-ui';
import Request from '@hotmart/request';

class FileService {
  events = GtagService.createEvents({
    common: {
      name: 'files-library-actions',
      category: CATEGORIES.CONTENT
    },
    open: {
      action: 'page-library-open'
    },
    select: {
      action: 'page-library-select'
    },
    localUpload: {
      action: 'page-local-upload'
    },
    libraryLocalUpload: {
      action: 'page-library-local-upload'
    }
  });

  fileStatus(fileId) {
    return Request('file.status', { fileId });
  }

  delete(entityId, fileId) {
    return Request('file.delete', { entityId, fileId });
  }

  fetchPrivateFileLink(fileId) {
    return Request('file.fetchPrivateLink', { fileId });
  }

  fetchByType(entityId, type) {
    return Request('file.fetchEntityByType', { entityId, type });
  }

  fetchMedias(entityId) {
    return this.fetchByType(entityId, ENTITY_TYPE.PAGE_MEDIA);
  }

  fetchAttachments(entityId) {
    return this.fetchByType(entityId, ENTITY_TYPE.PAGE_ATTACHMENT);
  }

  fetchPageBackgroundImg(entityId) {
    return this.fetchByType(entityId, ENTITY_TYPE.PAGE_BACKGROUND_IMG);
  }

  fetchQuizProfile(entityId) {
    return this.fetchByType(entityId, ENTITY_TYPE.QUIZ_PROFILE_IMG);
  }

  fetchModule(entityId, entityType) {
    return this.fetchByType(
      entityId,
      `${ENTITY_TYPE.MODULE_PURCHASE_DATA}_${entityType}`
    );
  }

  fetchSales(entityId, fileType) {
    return this.fetchByType(
      entityId,
      `${ENTITY_TYPE.MEMBERSHIP_PURCHASE_DATA}_${fileType}`
    );
  }

  fetchSalesMedia(entityId) {
    return this.fetchSales(entityId, ENTITY_DATA_TYPE.MEDIA);
  }

  fetchSalesImage(entityId) {
    return this.fetchSales(entityId, ENTITY_DATA_TYPE.IMAGE);
  }

  fetchRecommendation(entityId, entityType) {
    return this.fetchByType(
      entityId,
      `${ENTITY_TYPE.RECOMMENDATION}_${entityType}`
    );
  }

  fetchRecommendationMedia(entityId) {
    return this.fetchRecommendation(entityId, ENTITY_DATA_TYPE.MEDIA);
  }

  fetchRecommendationImage(entityId) {
    return this.fetchRecommendation(entityId, ENTITY_DATA_TYPE.IMAGE);
  }

  fetchModuleMedia(entityId) {
    return this.fetchModule(entityId, ENTITY_DATA_TYPE.MEDIA);
  }

  fetchModuleImage(entityId) {
    return this.fetchModule(entityId, ENTITY_DATA_TYPE.IMAGE);
  }

  fetchPersonaAvatar(entityId) {
    return this.fetchByType(entityId, ENTITY_TYPE.PERSONA_IMG);
  }

  orderFilesByFileOrder(values) {
    return orderBy(values || [], 'fileOrder');
  }

  mapFilesId(values) {
    return (values || []).map((value) => value.id || value.fileId);
  }

  getMediaOrderValue(media) {
    const MAX_FILE_ORDER = 1000;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const TIME_STAMP_LENGTH = Math.pow(10, 15);
    const time = new Date(media.creationDate).getTime();

    return (media.fileOrder || MAX_FILE_ORDER) * TIME_STAMP_LENGTH + time;
  }

  compareByFileOrderOrCreationDate(mediaA, mediaB) {
    const mediaOrderValueA = this.getMediaOrderValue(mediaA);
    const mediaOrderValueB = this.getMediaOrderValue(mediaB);

    if (mediaOrderValueA > mediaOrderValueB) {
      return 1;
    } else if (mediaOrderValueB > mediaOrderValueA) {
      return -1;
    }

    return 0;
  }

  sortMediasByOrderOrCreationDate(medias) {
    return (medias || []).sort(
      this.compareByFileOrderOrCreationDate.bind(this)
    );
  }

  updateTokenOnAllEntities(token) {
    updateTokenFromAllEntities(token);
  }

  handleFileException = (exception) => {
    const { response } = exception || {};

    if (response) {
      const customMessage = 'failed_upload';
      const timeout = 'none';

      UiService.handleInvalidToken(response, this.updateTokenOnAllEntities, {
        customMessage,
        timeout
      });
    }
  };

  createLibraryEvent(type, entityInfo) {
    return { ...this.events[type], label: entityInfo.type };
  }

  onLibraryOpen = ({ entityInfo }) => {
    GtagService.send(this.createLibraryEvent('open', entityInfo));
  };

  onLibraryUpload = ({ entityInfo }) => {
    GtagService.send(this.createLibraryEvent('select', entityInfo));
    sendDatahubEvent(UploadOrigin.Membership, entityInfo.type);
  };

  onLibraryLocalUpload = ({ entityInfo }) => {
    GtagService.send(this.createLibraryEvent('libraryLocalUpload', entityInfo));

    sendDatahubEvent(UploadOrigin.Device, entityInfo.type);
  };

  onLocalUpload = (entityType) => {
    GtagService.send(
      this.createLibraryEvent('localUpload', { type: entityType })
    );

    sendDatahubEvent(UploadOrigin.Device, entityType);
  };

  getDownloadUrl = async (fileId, entityType) => {
    try {
      const shouldDownloadAsAttachment =
        ENTITY_TYPE.PAGE_ATTACHMENT === entityType;
      const downloadUrl = await Request('file.downloadUrl', {
        fileId,
        addContentDispositionAttachment: shouldDownloadAsAttachment
      });

      return downloadUrl;
    } catch (e) {
      UiService.handleError(e);

      return null;
    }
  };

  getPublicUrl = async (fileId) => {
    try {
      const publicUrl = await Request('file.public.thumbor', {
        fileId
      });

      return publicUrl;
    } catch {
      return null;
    }
  };

  openFile = async (fileId, entityType) => {
    const downloadUrl = await this.getDownloadUrl(fileId, entityType);

    downloadUrl && window.open(downloadUrl);
  };

  isAttachmentFinished = (upload) => {
    const { origin, status, playerMedia } = upload;

    const isPlayerStatusFinished =
      playerMedia && FINISHED_PLAYER_ATTACHMENT.includes(playerMedia.status);

    return (
      origin === 'UPLOAD' &&
      (status === UPLOAD_STATUS.FINISHED || isPlayerStatusFinished)
    );
  };
}

export default new FileService();
